// src/services/labelItService.js
//
// Label It! games (Layout Builder ▸ 🏷️ Label It! games).
//
//   labelGames/{topic}   DRAFTS — admins only. Students never see these.
//   quizzes/{topic}      the LIVE game, written only by "Publish". Whatever
//                        game was there is copied to quizBackups first, the
//                        same way the Game generator does it.
//
// A board: { question, image, imageAlt, tiles: [{ id, text, image, x, y }] }
// x / y are percent of the picture's width / height (the drop spot's centre).

import { addDoc, collection, doc, getDocs, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";
import { exactKey } from "../utils/topicKey";

export const LABEL_GAME_TYPE = "labelIt";
const DRAFTS = "labelGames";

const who = () => {
  const u = getAuth().currentUser;
  return u?.email || u?.uid || "unknown";
};
const keyOf = (slug) => {
  const id = exactKey(slug);
  if (!id) throw new Error("A game needs a topic slug.");
  return id;
};
const round1 = (n) => Math.round(Math.max(0, Math.min(100, Number(n) || 0)) * 10) / 10;

/** Boards with only the fields the game reads (and no empty tiles). */
export const cleanBoards = (boards = []) =>
  (boards || []).map((b, bi) => ({
    question: String(b?.question || "").trim(),
    image: String(b?.image || "").trim(),
    imageAlt: String(b?.imageAlt || "").trim(),
    tiles: (b?.tiles || [])
      .filter((t) => String(t?.text || "").trim() || t?.image)
      .map((t, ti) => {
        const out = { id: String(t.id || `b${bi + 1}t${ti + 1}`), x: round1(t.x), y: round1(t.y) };
        if (String(t.text || "").trim()) out.text = String(t.text).trim();
        if (t.image) out.image = String(t.image);
        return out;
      }),
  }));

/** What has to be fixed before a game can be published. */
export const labelProblems = (boards = []) => {
  const out = [];
  if (!boards.length) out.push("Add a picture board.");
  boards.forEach((b, i) => {
    const n = boards.length > 1 ? `Picture ${i + 1}` : "The picture";
    if (!String(b.image || "").trim()) out.push(`${n} has no picture yet.`);
    const tiles = (b.tiles || []).filter((t) => String(t.text || "").trim() || t.image);
    if (tiles.length < 2) out.push(`${n} needs at least 2 tiles.`);
    if ((b.tiles || []).length !== tiles.length) out.push(`${n} has a tile with no words and no picture.`);
    const seen = new Set();
    tiles.forEach((t) => {
      const k = String(t.text || "").trim().toLowerCase();
      if (k && seen.has(k)) out.push(`${n} has two tiles called “${t.text}”.`);
      if (k) seen.add(k);
    });
  });
  return out;
};

/** topic key -> draft */
export const getLabelDrafts = async () => {
  const snap = await getDocs(collection(db, DRAFTS));
  const out = {};
  snap.forEach((d) => {
    out[d.id] = { id: d.id, ...d.data() };
  });
  return out;
};

export const saveLabelDraft = async (slug, { title, boards, bonusRoundType, status = "draft" }) => {
  const id = keyOf(slug);
  const payload = {
    slug: id,
    title: title || id,
    boards: cleanBoards(boards),
    bonusRoundType: bonusRoundType || "diamondTap",
    status,
    updatedAt: serverTimestamp(),
    updatedBy: who(),
  };
  await setDoc(doc(db, DRAFTS, id), payload, { merge: true });
  return payload;
};

/**
 * Makes the draft the topic's live 🎮 Game.
 * 1. Copies the current quizzes/{slug} (if any) into quizBackups.
 * 2. Writes quizzes/{slug} as a labelIt game.
 * 3. Marks the draft published.
 */
export const publishLabelGame = async ({ slug, title, boards, bonusRoundType }) => {
  const id = keyOf(slug);
  const ref = doc(db, "quizzes", id);
  const before = await getDoc(ref);
  let backupId = null;
  if (before.exists()) {
    const b = await addDoc(collection(db, "quizBackups"), {
      slug: id,
      quiz: before.data(),
      reason: "replaced by a Label It! publish",
      backedUpAt: serverTimestamp(),
      backedUpBy: who(),
    });
    backupId = b.id;
  }
  const questions = cleanBoards(boards);
  const quiz = {
    slug: id,
    title: title || id,
    gameType: LABEL_GAME_TYPE,
    bonusRoundType: bonusRoundType || "diamondTap",
    questions,
    madeWith: "labelIt",
    publishedAt: serverTimestamp(),
    publishedBy: who(),
  };
  // Puzzle Shuffle (bonus) needs a picture: the first board is the natural one
  if (questions[0]?.image) quiz.placeholderImage = questions[0].image;
  await setDoc(ref, quiz);
  await saveLabelDraft(id, { title, boards, bonusRoundType, status: "published" });
  return { backupId, count: questions.length, replaced: before.exists() ? before.data()?.gameType || "a game" : null };
};
